import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Landmark } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';

const EMPTY_BANK_FORM = {
  accountHolderName: '',
  accountNumber: '',
  ifscCode: '',
  bankName: '',
  upiId: ''
};

// Buying Premium promotes the user to author automatically
// (server/src/routes/membership.js) — this modal no longer gates that
// role flip behind consent, it just collects the optional payout details.
export default function BecomeAuthorModal({ open, onClose, onConfirmed }) {
  const [bankForm, setBankForm] = useState(EMPTY_BANK_FORM);
  const [bankErrors, setBankErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validateBankForm = (values) => {
    const errs = {};
    if (!values.accountHolderName.trim()) errs.accountHolderName = 'Account holder name is required';
    if (!values.accountNumber.trim()) errs.accountNumber = 'Account number is required';
    else if (!/^\d{9,18}$/.test(values.accountNumber.trim())) errs.accountNumber = 'Enter a valid account number (9-18 digits)';
    if (!values.ifscCode.trim()) errs.ifscCode = 'IFSC code is required';
    else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(values.ifscCode.trim().toUpperCase())) errs.ifscCode = 'Enter a valid IFSC code';
    if (!values.bankName.trim()) errs.bankName = 'Bank name is required';
    if (values.upiId.trim() && !/^[\w.-]{2,256}@[a-zA-Z][a-zA-Z0-9]{2,64}$/.test(values.upiId.trim())) errs.upiId = 'Enter a valid UPI ID';
    return errs;
  };

  const reset = () => {
    setBankForm(EMPTY_BANK_FORM);
    setBankErrors({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const confirm = async (bankDetails) => {
    setSubmitting(true);
    try {
      const data = await api.post('/users/become-author', bankDetails ? { bankDetails } : {});
      toast.success('You are now an author on GeniusMind!', { icon: '🎉' });
      onConfirmed(data.user);
      reset();
    } catch (err) {
      toast.error(err.message || 'Could not complete author setup');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveBank = (e) => {
    e.preventDefault();
    const errs = validateBankForm(bankForm);
    setBankErrors(errs);
    if (Object.keys(errs).length > 0) return;
    confirm({
      ...bankForm,
      accountHolderName: bankForm.accountHolderName.trim(),
      bankName: bankForm.bankName.trim(),
      ifscCode: bankForm.ifscCode.trim().toUpperCase(),
      upiId: bankForm.upiId.trim()
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm"
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl2 bg-white dark:bg-surface-darkCard p-6 shadow-soft-lg sm:p-8"
          >
            <button
              onClick={handleClose}
              aria-label="Close"
              className="absolute right-4 top-4 rounded-full p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
            >
              <X size={18} />
            </button>

            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-primary text-white shadow-glow">
              <Landmark size={20} />
            </span>
            <h3 className="mt-4 font-display text-xl font-semibold text-slate-900 dark:text-white">
              Add payout details
            </h3>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Add your bank account to receive royalty payouts. You can skip this and add it later from
              your Account page.
            </p>

            <form onSubmit={handleSaveBank} className="mt-4 space-y-3" noValidate>
                  <div>
                    <input
                      type="text"
                      value={bankForm.accountHolderName}
                      onChange={(e) => { setBankForm({ ...bankForm, accountHolderName: e.target.value }); setBankErrors((p) => ({ ...p, accountHolderName: '' })); }}
                      placeholder="Account holder name"
                      className={`input-field w-full text-sm ${bankErrors.accountHolderName ? 'border-red-400 focus:border-red-500 focus:ring-red-100 dark:focus:ring-red-900' : ''}`}
                    />
                    {bankErrors.accountHolderName && <p className="mt-1 text-xs text-red-500">{bankErrors.accountHolderName}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={bankForm.accountNumber}
                      onChange={(e) => { setBankForm({ ...bankForm, accountNumber: e.target.value.replace(/\D/g, '') }); setBankErrors((p) => ({ ...p, accountNumber: '' })); }}
                      placeholder="Account number"
                      className={`input-field w-full text-sm ${bankErrors.accountNumber ? 'border-red-400 focus:border-red-500 focus:ring-red-100 dark:focus:ring-red-900' : ''}`}
                    />
                    {bankErrors.accountNumber && <p className="mt-1 text-xs text-red-500">{bankErrors.accountNumber}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={bankForm.ifscCode}
                      onChange={(e) => { setBankForm({ ...bankForm, ifscCode: e.target.value.toUpperCase() }); setBankErrors((p) => ({ ...p, ifscCode: '' })); }}
                      placeholder="IFSC code"
                      className={`input-field w-full text-sm uppercase ${bankErrors.ifscCode ? 'border-red-400 focus:border-red-500 focus:ring-red-100 dark:focus:ring-red-900' : ''}`}
                    />
                    {bankErrors.ifscCode && <p className="mt-1 text-xs text-red-500">{bankErrors.ifscCode}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={bankForm.bankName}
                      onChange={(e) => { setBankForm({ ...bankForm, bankName: e.target.value }); setBankErrors((p) => ({ ...p, bankName: '' })); }}
                      placeholder="Bank name"
                      className={`input-field w-full text-sm ${bankErrors.bankName ? 'border-red-400 focus:border-red-500 focus:ring-red-100 dark:focus:ring-red-900' : ''}`}
                    />
                    {bankErrors.bankName && <p className="mt-1 text-xs text-red-500">{bankErrors.bankName}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={bankForm.upiId}
                      onChange={(e) => { setBankForm({ ...bankForm, upiId: e.target.value }); setBankErrors((p) => ({ ...p, upiId: '' })); }}
                      placeholder="UPI ID (optional)"
                      className={`input-field w-full text-sm ${bankErrors.upiId ? 'border-red-400 focus:border-red-500 focus:ring-red-100 dark:focus:ring-red-900' : ''}`}
                    />
                    {bankErrors.upiId && <p className="mt-1 text-xs text-red-500">{bankErrors.upiId}</p>}
                  </div>

                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => confirm(null)}
                      disabled={submitting}
                      className="btn-secondary !px-4 py-2 text-sm"
                    >
                      Skip for now
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary !px-4 py-2 text-sm"
                    >
                      {submitting ? 'Saving...' : 'Save & Finish'}
                    </button>
                  </div>
            </form>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
